// "Open the store": one employee opens the app for the day with their 4-digit ID.
// The device itself is paired once with the store login (a Supabase session that persists).
import { supabase } from '../lib/supabase'
import type { Employee } from '../lib/types'

const KEY = 'starchive.day'
const LAST = 'starchive.lastTouch'
const MAX_HOURS = 14
/** Idle lock: after this long with no touch the app locks and asks for an employee ID again. */
export const IDLE_MINUTES = 5

export interface DayState { employee_id: string; name: string; date: string; openedAt: number }

export const nyToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })

export function readDay(): DayState | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const d = JSON.parse(raw) as DayState
    if (d.date !== nyToday() || Date.now() - d.openedAt > MAX_HOURS * 3600_000) { localStorage.removeItem(KEY); return null }
    const last = Number(localStorage.getItem(LAST) || d.openedAt)
    if (Date.now() - last > IDLE_MINUTES * 60_000) { localStorage.removeItem(KEY); return null }   // sat idle: lock
    return d
  } catch { return null }
}

export async function openDay(e: Employee): Promise<DayState> {
  const d: DayState = { employee_id: e.employee_id, name: e.name, date: nyToday(), openedAt: Date.now() }
  try { localStorage.setItem(KEY, JSON.stringify(d)); localStorage.setItem(LAST, String(Date.now())) } catch { /* private mode */ }
  await supabase.from('audit_log').insert({ employee_id: e.employee_id, action: 'day.open', entity: 'device', entity_id: null, detail: { ua: navigator.userAgent.slice(0, 80) } }).then(() => {}, () => {})
  return d
}

export async function lockDay(d: DayState | null) {
  try { localStorage.removeItem(KEY) } catch { /* ignore */ }
  if (d) await supabase.from('audit_log').insert({ employee_id: d.employee_id, action: 'day.lock', entity: 'device', entity_id: null }).then(() => {}, () => {})
}

/** Called on every touch; the timestamp is what the idle check reads, in this tab and after a relaunch. */
let lastWrite = 0
export function touchDay() {
  const now = Date.now()
  if (now - lastWrite < 5000) return   // D6: at most every 5 s (it was every scroll event); the lock's own timer is unchanged
  lastWrite = now
  try { localStorage.setItem(LAST, String(now)) } catch { /* ignore */ }
}
